import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid, Card, CardContent, Typography, Box, Skeleton,
} from '@mui/material';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Layout from '../../components/Layout/Layout';
import StatCard from '../../components/Common/StatCard';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import { getPublisherDashboardApi } from '../../api/dashboard';
import { getMyAgreementApi } from '../../api/auth';
import { getBookFilterOptionsApi } from '../../api/books';
import { TARGET_SEGMENTS_LIQVID } from '../../utils/constants';

const COLORS = ['#1565c0', '#f57c00', '#2e7d32', '#c62828', '#6a1b9a', '#00838f', '#4e342e', '#558b2f', '#00695c', '#ad1457'];

function PieLegend({ data }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <Box sx={{ mt: 1.5, maxHeight: 130, overflowY: 'auto', pr: 0.5 }}>
      {data.map((entry, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Box sx={{ width: 11, height: 11, borderRadius: '2px', bgcolor: COLORS[i % COLORS.length], flexShrink: 0 }} />
          <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.3 }}>{entry.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
            {entry.count} ({total > 0 ? ((entry.count / total) * 100).toFixed(0) : 0}%)
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function LangPieCard({ title, total, chartData, accentColor }) {
  const langLabel = title.replace(' Language', '');
  const emptyMsg = langLabel
    ? `No ${langLabel} language books yet`
    : 'No books with language origin tagged yet';
  return (
    <Card sx={{ height: '100%', borderTop: `4px solid ${accentColor}` }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {total} books
        </Typography>
        {chartData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 150, textAlign: 'center', px: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {emptyMsg}
            </Typography>
          </Box>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={65}>
                  {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(val, name) => [val, name]} />
              </PieChart>
            </ResponsiveContainer>
            <PieLegend data={chartData} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PieCard({ title, chartData }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        {chartData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
            <Typography variant="body2" color="text.secondary">No data available</Typography>
          </Box>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={85}>
                  {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(val, name) => [val, name]} />
              </PieChart>
            </ResponsiveContainer>
            <PieLegend data={chartData} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AgreementBadge({ agreement }) {
  const isApproved = agreement?.status === 'Approved';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      border: 2, borderColor: isApproved ? 'success.main' : 'warning.main',
      borderRadius: 2, px: 2, py: 1,
    }}>
      {isApproved
        ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
        : <AccessTimeIcon sx={{ color: 'warning.main', fontSize: 24 }} />}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
          Onboarding Agreement
        </Typography>
        <Typography variant="body2" fontWeight="bold" color={isApproved ? 'success.main' : 'warning.main'}>
          {isApproved
            ? `Approved · ${agreement?.approval_date ? new Date(agreement.approval_date).toLocaleDateString() : ''}`
            : 'Pending admin approval'}
        </Typography>
      </Box>
    </Box>
  );
}

export default function PublisherDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreement, setAgreement] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});

  useEffect(() => {
    getMyAgreementApi().then((r) => setAgreement(r.data)).catch(() => setAgreement({ status: 'Pending' }));
    getBookFilterOptionsApi().then((r) => {
      setDisciplines(r.data.disciplines || []);
      setLanguages(r.data.languages || []);
    }).catch(() => {});
  }, []);

  const fetchDashboard = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await getPublisherDashboardApi(params);
    setData(r.data);
    setLoading(false);
  }, [search, filters]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const filterConfig = [
    { key: 'discipline', label: 'Discipline', type: 'select', options: disciplines },
    { key: 'language', label: 'Language', type: 'select', options: languages },
    { key: 'target_segment_liqvid', label: 'Target Segment (Liqvid)', type: 'select', options: TARGET_SEGMENTS_LIQVID },
    { key: 'author_name', label: 'Author', type: 'text' },
  ];

  if (loading) return (
    <Layout title="Publisher Dashboard">
      <PageHeader title="Publisher Dashboard" subtitle="Your book publishing overview" />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[...Array(4)].map((_, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card><CardContent><Skeleton variant="text" width="60%" /><Skeleton variant="rectangular" height={48} sx={{ my: 1 }} /><Skeleton variant="text" width="40%" /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[...Array(3)].map((_, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card><CardContent><Skeleton variant="text" width="50%" sx={{ mb: 1 }} /><Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', my: 2 }} /><Skeleton variant="text" /><Skeleton variant="text" width="80%" /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        {[...Array(2)].map((_, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card><CardContent><Skeleton variant="text" width="50%" sx={{ mb: 1 }} /><Skeleton variant="rectangular" height={200} /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
    </Layout>
  );

  const { summary } = data || {};

  const toChartData = (arr, key) => (arr || []).map(item => ({ name: item[key] || 'Unknown', count: item.count }));

  const langOriginData = toChartData(data?.books_by_language_origin, 'language_origin');
  const langOriginTotal = langOriginData.reduce((s, d) => s + d.count, 0);
  const foreignData = toChartData(data?.books_by_foreign_lang, 'language');
  const indianData = toChartData(data?.books_by_indian_lang, 'language');
  const targetSegData = toChartData(data?.books_by_target_segment_liqvid, 'target_segment_liqvid');
  const disciplineData = toChartData(data?.books_by_discipline, 'discipline');

  return (
    <Layout title="Publisher Dashboard">
      <PageHeader
        title="Publisher Dashboard"
        subtitle="Your book publishing overview"
        action={<AgreementBadge agreement={agreement} />}
      />

      <SearchFilterBar
        searchPlaceholder="Search by ISBN, title, author, discipline..."
        filters={filterConfig}
        onSearch={setSearch}
        onFilter={setFilters}
      />

      {/* Stat cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Total Books" value={summary?.total_books} icon={<LibraryBooksIcon />} color="primary" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Admin Uploaded" value={summary?.admin_uploaded} icon={<CloudUploadIcon />} color="secondary" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/publisher/submissions')}>
            <StatCard title="Files Uploaded" value={summary?.files_uploaded} icon={<CheckCircleIcon />} color="success" />
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/publisher/submissions')}>
            <StatCard title="File Upload Pending" value={summary?.file_upload_pending} icon={<AccessTimeIcon />} color="warning" />
          </Box>
        </Grid>
      </Grid>

      {/* Language cards row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <LangPieCard
            title="Language"
            total={langOriginTotal}
            chartData={langOriginData}
            accentColor="#1565c0"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <LangPieCard
            title="Foreign Language"
            total={data?.books_by_foreign_count || 0}
            chartData={foreignData}
            accentColor="#f57c00"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <LangPieCard
            title="Indian Language"
            total={data?.books_by_indian_count || 0}
            chartData={indianData}
            accentColor="#2e7d32"
          />
        </Grid>
      </Grid>

      {/* Books by Target Segment + Books by Discipline */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <PieCard title="Books by Target Segment" chartData={targetSegData} />
        </Grid>
        <Grid item xs={12} md={6}>
          <PieCard title="Books by Discipline" chartData={disciplineData} />
        </Grid>
      </Grid>
    </Layout>
  );
}
